import React, {useEffect, useState} from 'react';
import {BrowserRouter, Routes, Route, useParams, Navigate, useLocation} from 'react-router-dom';
import {useStoreState, useStoreActions} from '@/store';
import {getComponent} from './lazyComponentRegistry';
import {PageContainer} from 'components/page/PageContainer';
import {DndProvider} from 'react-dnd';
import {HTML5Backend} from 'react-dnd-html5-backend';
import TabEnabledPageContainer from '@/components/navigation/TabEnabledPageContainer';
import BreadcrumbWrapper from '@/components/navigation/BreadcrumbWrapper';
import GeneralRedirectView from '../views/insights/GeneralRedirectView';

import useOptimizedConfig from '@/hooks/useOptimizedConfig';
import i18n from '@/i18n';
// Helper function to get URL patterns for React Router
const getRoutePattern = (patternName: string, fallback: string, paramReplacements?: Record<string, string>) => {
    let pattern = window.URLS?.[patternName] || fallback;

    // Apply parameter name replacements for React Router compatibility
    if (paramReplacements) {
        Object.entries(paramReplacements).forEach(([from, to]) => {
            pattern = pattern.replace(`:${from}`, `:${to}`);
        });
    }

    return pattern;
};

interface AppConfigs {
    [key: string]: any;
}

const BaseRouter: React.FC = () => {
    const [autoRoutes, setAutoRoutes] = useState<React.ReactElement[]>([]);
    const [forceReload, setForceReload] = useState(0);
    const [routesLoaded, setRoutesLoaded] = useState(false);

    // Load configuration first
    const {config: appConfigs, loading, error} = useOptimizedConfig();

    // Get menu structure from the sidebar menu store and trigger loading
    const menuStructure = useStoreState(state => state.sidebarMenu.menuStructure);
    const isMenuLoading = useStoreState(state => state.sidebarMenu.isLoading);
    const loadMenuStructure = useStoreActions(actions => actions.sidebarMenu.loadMenuStructure);
    const clearMenuCache = useStoreActions(actions => actions.sidebarMenu.clearMenuCache);

    // Trigger menu loading only after config is loaded
    useEffect(() => {
        if (!loading && appConfigs) {
            loadMenuStructure();
        }
    }, [loadMenuStructure, loading, appConfigs]);

    // Expose reload function globally for cache clearing
    useEffect(() => {
        (window as any).reloadRoutes = () => {
            clearMenuCache();
            setForceReload(prev => prev + 1);
            loadMenuStructure();
        };
        // Also expose current routes for debugging
        (window as any).getCurrentRoutes = () => {
            return autoRoutes;
        };
        return () => {
            delete (window as any).reloadRoutes;
            delete (window as any).getCurrentRoutes;
        };
    }, [autoRoutes, clearMenuCache, loadMenuStructure]);

    useEffect(() => {
        const loadAutoRoutes = () => {
            try {
                if (!menuStructure || menuStructure.length === 0) {
                    return;
                }

                const routes: React.ReactElement[] = [];

                const processMenuItem = (menuItem: any, itemPath: string, isSection: boolean) => {
                    // Handle items with tab navigation
                    if (menuItem.meta.hasTabNavigation && menuItem.meta.tabs) {
                        // Create redirect component helper
                        const createRedirect = (fromPath: string, toPath: string, keySuffix: string) => {
                            const RedirectWithParams = () => {
                                const location = useLocation();
                                const targetUrl = `${toPath}${location.search}`;
                                return <Navigate to={targetUrl} replace />;
                            };

                            routes.push(
                                <Route
                                    key={`auto-${itemPath}-${keySuffix}`}
                                    path={fromPath}
                                    element={<RedirectWithParams />}
                                />
                            );
                        };

                        // Resolve parameter placeholders in a route using default values
                        const resolveDefaultParams = (route: string): string => {
                            if (!menuItem.meta.defaultParameters) return route;
                            let resolved = route;
                            for (const [param, value] of Object.entries(menuItem.meta.defaultParameters)) {
                                resolved = resolved.replace(`:${param}`, value as string);
                            }
                            return resolved;
                        };

                        // Create base route redirect to default tab with query parameter preservation
                        if (menuItem.meta.routes?.base && menuItem.meta.defaultTab) {
                            const defaultTab = menuItem.meta.tabs.find((tab: any) => tab.key === menuItem.meta.defaultTab);
                            if (defaultTab) {
                                const resolvedRoute = resolveDefaultParams(defaultTab.route);
                                createRedirect(menuItem.meta.routes.base, resolvedRoute, 'redirect');

                                // Also redirect routes.list if it's different from base and no viewComponent
                                if (menuItem.meta.routes?.list &&
                                    menuItem.meta.routes.list !== menuItem.meta.routes.base &&
                                    !menuItem.meta.viewComponent) {
                                    createRedirect(menuItem.meta.routes.list, resolvedRoute, 'list-redirect');
                                }
                            }
                        }

                        // Create routes for each tab
                        for (const tab of menuItem.meta.tabs) {
                            const TabComponent = getComponent(tab.viewComponent);

                            if (TabComponent) {
                                // Create wrapper to extract parameters from the route
                                const TabWrapper = () => {
                                    const params = useParams();

                                    return (
                                        <TabEnabledPageContainer menuItem={menuItem}>
                                            <TabComponent params={params} />
                                        </TabEnabledPageContainer>
                                    );
                                };

                                routes.push(
                                    <Route
                                        key={`auto-${itemPath}-tab-${tab.key}`}
                                        path={tab.route}
                                        element={<TabWrapper />}
                                    />
                                );
                            }
                        }

                        // If wildcardRoute is enabled for tabbed items, create a wildcard route
                        if (menuItem.meta.wildcardRoute && menuItem.meta.routes?.detail && menuItem.meta.viewComponent) {
                            const WildcardComponent = getComponent(menuItem.meta.viewComponent);

                            if (WildcardComponent) {
                                const WildcardWrapper = () => {
                                    const params = useParams();

                                    const processedParams = {...params};

                                    if (params['*'] && menuItem.meta.parameters) {
                                        const wildcardPath = params['*'];
                                        const segments = wildcardPath.split('/').filter(Boolean);

                                        menuItem.meta.parameters.forEach((paramName: string, index: number) => {
                                            if (segments[index]) {
                                                processedParams[paramName] = segments[index];
                                            }
                                        });
                                    }

                                    return (
                                        <TabEnabledPageContainer menuItem={menuItem}>
                                            <WildcardComponent params={processedParams} />
                                        </TabEnabledPageContainer>
                                    );
                                };

                                routes.push(
                                    <Route
                                        key={`auto-${itemPath}-wildcard`}
                                        path={`${menuItem.meta.routes.detail}/*`}
                                        element={<WildcardWrapper />}
                                    />
                                );
                            }
                        }

                        // Generate list route for tabbed items that have a viewComponent (list view)
                        if (menuItem.meta.routes?.list && menuItem.meta.viewComponent) {
                            const ListComponent = getComponent(menuItem.meta.viewComponent);

                            if (ListComponent) {
                                const ListWrapper = () => {
                                    const params = useParams();
                                    const element = <ListComponent params={params} />;

                                    return menuItem.meta.showBreadcrumb ? (
                                        <BreadcrumbWrapper menuItem={menuItem}>
                                            {element}
                                        </BreadcrumbWrapper>
                                    ) : (
                                        element
                                    );
                                };

                                routes.push(
                                    <Route
                                        key={`auto-${itemPath}-list`}
                                        path={menuItem.meta.routes.list}
                                        element={<ListWrapper />}
                                    />
                                );
                            }
                        }

                        // Generate detail routes for tabbed items that have a detailViewComponent
                        if (menuItem.meta.routes?.detail && menuItem.meta.detailViewComponent) {
                            const DetailComponent = getComponent(menuItem.meta.detailViewComponent);

                            if (DetailComponent) {
                                const DetailWrapper = () => {
                                    const params = useParams();

                                    const element = menuItem.meta.showBreadcrumb ? (
                                        <BreadcrumbWrapper menuItem={menuItem}>
                                            <DetailComponent params={params} />
                                        </BreadcrumbWrapper>
                                    ) : (
                                        <DetailComponent params={params} />
                                    );

                                    return element;
                                };

                                // For items with tabs, create routes that match all tab paths
                                // This allows the detail component to handle tab switching internally
                                routes.push(
                                    <Route
                                        key={`auto-${itemPath}-detail-tabs`}
                                        path={`${menuItem.meta.routes.detail}/*`}
                                        element={<DetailWrapper />}
                                    />
                                );

                                // Also create the base detail route (without tab suffix)
                                routes.push(
                                    <Route
                                        key={`auto-${itemPath}-detail`}
                                        path={menuItem.meta.routes.detail}
                                        element={<DetailWrapper />}
                                    />
                                );
                            }
                        }
                    }
                    // Handle regular items (existing logic)
                    else if (menuItem.meta.routes && menuItem.meta.viewComponent) {
                        const Component = getComponent(menuItem.meta.viewComponent);

                        if (Component) {
                            // Generate list route
                            if (menuItem.meta.routes.list) {
                                const ListWrapper = () => {
                                    const params = useParams();
                                    const component = <Component params={params} />;

                                    return menuItem.meta.showBreadcrumb ? (
                                        <BreadcrumbWrapper menuItem={menuItem}>
                                            {component}
                                        </BreadcrumbWrapper>
                                    ) : (
                                        component
                                    );
                                };

                                routes.push(
                                    <Route
                                        key={`auto-${itemPath}-list`}
                                        path={menuItem.meta.routes.list}
                                        element={<ListWrapper />}
                                    />
                                );
                            }

                            // Generate detail route if exists
                            if (menuItem.meta.routes.detail) {
                                const DetailWrapper = () => {
                                    const params = useParams();

                                    const processedParams = {...params};

                                    if (menuItem.meta.wildcardRoute && params['*'] && menuItem.meta.parameters) {
                                        const wildcardPath = params['*'];
                                        const segments = wildcardPath.split('/').filter(Boolean);

                                        menuItem.meta.parameters.forEach((paramName: string, index: number) => {
                                            if (segments[index]) {
                                                processedParams[paramName] = segments[index];
                                            }
                                        });
                                    }

                                    const component = <Component params={processedParams} />;

                                    // Wrap with breadcrumbs if showBreadcrumb is true
                                    return menuItem.meta.showBreadcrumb ? (
                                        <BreadcrumbWrapper menuItem={menuItem}>
                                            {component}
                                        </BreadcrumbWrapper>
                                    ) : (
                                        component
                                    );
                                };

                                if (menuItem.meta.wildcardRoute) {
                                    routes.push(
                                        <Route
                                            key={`auto-${itemPath}-detail-wildcard`}
                                            path={`${menuItem.meta.routes.detail}/*`}
                                            element={<DetailWrapper />}
                                        />
                                    );
                                }

                                if (menuItem.meta.parametrized && menuItem.meta.parameters) {
                                    const params = menuItem.meta.parameters.map((p: string) => `:${p}`).join('/');
                                    const parametrizedPath = `${menuItem.meta.routes.detail}/${params}`;

                                    routes.push(
                                        <Route
                                            key={`auto-${itemPath}-detail-params`}
                                            path={parametrizedPath}
                                            element={<DetailWrapper />}
                                        />
                                    );
                                }

                                routes.push(
                                    <Route
                                        key={`auto-${itemPath}-detail`}
                                        path={menuItem.meta.routes.detail}
                                        element={<DetailWrapper />}
                                    />
                                );
                            }
                        }
                    }
                };

                for (const section of menuStructure) {
                    // Process sections that are direct links (have routes/tabs configured)
                    const isSectionDirectLink = !!(
                        section.meta.routes?.base ||
                        section.meta.routes?.list ||
                        section.meta.hasTabNavigation ||
                        section.meta.breadcrumbs
                    );

                    if (isSectionDirectLink && (section.meta.routes || section.meta.hasTabNavigation)) {
                        processMenuItem(section, section.name, true);
                    }

                    // Process items within sections
                    for (const item of section.items) {
                        processMenuItem(item, item.path, false);
                    }
                }

                setAutoRoutes(routes);
                setRoutesLoaded(true);
            } catch (error) {
                console.error('Route generation failed:', error);
                setAutoRoutes([]);
                setRoutesLoaded(true); // Still set to true to prevent infinite loading
            }
        };

        // Only generate routes if we have menu structure data and it's not loading
        if (menuStructure && menuStructure.length > 0 && !isMenuLoading) {
            loadAutoRoutes();
        }
    }, [menuStructure, forceReload, isMenuLoading]); // React to changes in menuStructure AND loading state

    return (
        <BrowserRouter future={{v7_startTransition: true, v7_relativeSplatPath: true}}>
            <PageContainer>
                <DndProvider backend={HTML5Backend}>
                    {!routesLoaded || isMenuLoading || loading ? (
                        <div style={{padding: '2rem', textAlign: 'center'}}>
                            <div>{i18n.t('jt_loading_routes.text')}</div>
                            <div style={{fontSize: '0.875rem', color: '#6c757d', marginTop: '0.5rem'}}>
                                {loading ? i18n.t('jt_loading_configuration.text') :
                                    isMenuLoading ? i18n.t('jt_loading_menu_structure.text') :
                                        i18n.t('jt_generating_routes.text')}
                            </div>
                            {error && (
                                <div style={{color: '#dc3545', marginTop: '0.5rem'}}>
                                    {i18n.t('jt_error.text')}
                                </div>
                            )}
                        </div>
                    ) : (
                        <Routes>
                            <Route
                                path="/app/insights/special/general"
                                element={<GeneralRedirectView />}
                            />

                            {autoRoutes}
                        </Routes>
                    )}
                </DndProvider>
            </PageContainer>
        </BrowserRouter>
    );
};

export default BaseRouter;